import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CauseSelector from "../components/CauseSelector";
import { useAppState } from "../../../contexts/UserContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useTeam } from "../../../contexts/TeamContext";
import { useCheckIn } from "../checkin-context";
import { rewards } from "../rewards";
import styles from "../CheckIn.module.css";
import { useCheckInSubmission } from "../hooks/useCheckInSubmission";
import { CheckInLocationState } from "../CheckInView/CheckInView";
import type { CheckInCauseLike } from "../hooks/useCheckInViewHandlers";
import { getCauseTypes } from "@/api/causeTypeApi";
import i18n from "i18next";

export type CheckInAddProps = {
  stateOverride?: CheckInLocationState;
  onClose?: () => void;
};

/** @param {{ stateOverride?: any, onClose?: () => void }} [props] */
export const CheckInAdd = ({
  stateOverride,
  onClose,
}: CheckInAddProps = {}) => {
  const { setMoods, isOffline, refreshData, refreshTeamMoods, clearCache } =
    useAppState();
  const { userId } = useAuth();
  const { organizationId, teamId } = useTeam();
  const location = useLocation();
  const {
    causesToOffer,
    feelingName,
    selectedDate,
    selectCause,
    clearCauses,
    setCausesToOffer,
    setCausesLoading,
  } = useCheckIn();
  const mergedState = stateOverride ?? location.state ?? {};
  const mergedStateForExisting = mergedState ?? {};

  const [step, setStep] = useState("cause");
  const [rewardIdx, setRewardIdx] = useState<number | null>(null);
  const [yourOwnCause, setYourOwnCause] = useState<string>("");
  const [yourOwnCauseTypeId, setYourOwnCauseTypeId] = useState<string>("");
  const existingRecord = mergedStateForExisting.existingRecord;
  const [selectedCause, setSelectedCause] = useState(null);
  const navigate = useNavigate();
  const hasRunRef = useRef(false);

  function feelingIdToName(feelingId: string) {
    if (feelingId === "2") {
      return "Anxiety";
    }
    if (feelingId === "1") {
      return "Flow";
    }
    if (feelingId === "3") {
      return "Calm";
    }
    if (feelingId === "4") {
      return "Apathy";
    }
  }

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    setCausesLoading(true);
    getCauseTypes(teamId)
      .then((rows) => {

        console.log("f causes team id " + teamId)
        if (cancelled) return;

        let  offered = (Array.isArray(rows) ? rows : []).map((row: any) => ({
          id: String(row.id),
          feelingId: row.feelingId,
          causeTypeTranslation: { [i18n.language]: row.name },
        }));
        offered = offered.filter(x => feelingIdToName(x.feelingId) === feelingName);
        setCausesToOffer(offered);
      })
      .catch((err) => {
        console.error("CheckInAdd: failed to load causes:", err);
        if (!cancelled) setCausesToOffer([]);
      })
      .finally(() => {
        if (!cancelled) setCausesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, setCausesToOffer, setCausesLoading]);

  const handleProceed = useCheckInSubmission({
    existingRecord,
    selectedDate,
    feelingName,
    causes: causesToOffer,
    userId,
    organizationId,
    teamId,
    step,
    yourOwnCause,
    yourOwnCauseTypeId,
    isOffline,
    setMoods,
    clearCache,
    refreshTeamMoods,
    navigate,
    setStep,
  });

  useEffect(() => {
    if (!userId) return;
    refreshData(userId, "causeTypes").catch((err: any) => {
      console.error("CheckInAdd: refresh cause types failed:", err);
    });
  }, [userId, refreshData]);



  const handleCauseSelect = async (
    causeValue: CheckInCauseLike,
  ) => {
    if (causeValue.causeValue === "skip") {
      clearCauses();

      if (onClose) {
        console.log("onclose exists")
        onClose();
      } else {
        console.log("onclose does not exist")
      }

      return;
    }

    selectCause(causeValue as Parameters<typeof selectCause>[0]);
  };

  useEffect(() => {
    const isPostSubmitRewardStep = step === "reward" || step === "demo-reward";
    if (!isPostSubmitRewardStep || hasRunRef.current) return;
    if (rewardIdx == null) return;

    hasRunRef.current = true;

    // The feeling is already submitted from onFinish (with the clicked causeTypeId).
    setTimeout(() => {
      onClose?.();
    }, 2500);
  }, [step, rewardIdx, onClose]);

  return (
    <>
      {
        <>
          {step === "cause" && (
            <p className={styles["duration-text"]}>
              {`What has caused your ${feelingName?.toLocaleLowerCase()}?`}
            </p>
          )}

          {(step === "reward" || step === "demo-reward") &&
            rewardIdx != null &&
            rewards[rewardIdx] && (
              <>
                <p className={styles["duration-text"]}>
                  {rewards[rewardIdx].name}
                </p>

                <br />
                <br />

                <div style={{ display: "flex", justifyContent: "center" }}>
                  <img
                    src={rewards[rewardIdx].src}
                    alt=""
                    width="150"
                    height="200"
                  />
                </div>
              </>
            )}

          {step === "cause" && (
            <>
              <div className="cause-section-container">
                <>
                  <CauseSelector
                    userId={userId}
                    causesToOffer={causesToOffer}
                    existingRecord={existingRecord}
                    handleCauseSelect={handleCauseSelect}
                    selectedCause={selectedCause}
                    selectedCauses={[]}
                    onClose={onClose}
                    yourOwnCause={yourOwnCause}
                    setYourOwnCause={setYourOwnCause}
                    setYourOwnCauseTypeId={setYourOwnCauseTypeId}
                    onFinish={(causeTypeId?: string) => {
                      setRewardIdx(Math.floor(Math.random() * rewards.length));
                      handleProceed(causeTypeId)
                      setStep("reward")
                      localStorage.setItem("checkIn", "done");
                    }}
                  />
                </>
              </div>
            </>
          )}
        </>
      }
    </>
  );
};
